/**
 * App-wide desktop shell (web ≥1024px). The persistent left sidebar — brand
 * lockup, grouped nav, theme toggle, account row — now wraps EVERY authenticated
 * resident route from the ROOT layout (not just the tab roots), so pushed screens
 * (issues, vendors, classifieds, staff, gate pass…) sit inside the same sidebar +
 * capped content column as Home/Community instead of rendering as full-width
 * boxed pages. Public/auth/admin routes opt out via `noShellРath`.
 */
import { router, usePathname } from 'expo-router';
import React from 'react';
import { Platform, Pressable, ScrollView, View, useWindowDimensions } from 'react-native';

import { space } from '@bero/tokens';
import { ArchAvatar } from '@/features/community/bits';
import { Icon } from '@/features/account-admin/icons';
import { SocietySwitcher } from '@/components/SocietySwitcher';
import { InShellProvider } from '@/lib/in-shell';
import { useSession } from '@/lib/session';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { useTheme } from '@/theme';
import { AppText, BrandLockup, Hairline } from '@/ui';

export const WIDE = 1024;

// [i18n key, path] — labels resolve via t() so the sidebar localises too.
const NAV_MAIN: readonly (readonly [DictKey, string])[] = [
  ['tab_home', '/home'],
  ['tab_community', '/community'],
  ['tab_gate', '/gate'],
  ['tab_pay', '/pay'],
  ['nav_profile', '/profile'],
];

const NAV_COMMUNITY: readonly (readonly [DictKey, string])[] = [
  ['nav_vendors', '/vendors'],
  ['nav_classifieds', '/classifieds'],
  ['nav_lost_found', '/lost-found'],
  ['nav_birthdays', '/birthdays'],
  ['nav_elections', '/elections'],
];

const NAV_GATE: readonly (readonly [DictKey, string])[] = [
  ['nav_preapprove', '/preapprove'],
  ['nav_gate_pass', '/gatepass'],
  ['nav_visitor_history', '/visitors'],
  ['nav_household_staff', '/staff'],
  ['nav_parcels', '/parcels'],
  ['nav_vehicles', '/vehicles'],
];

const NAV_LIVING: readonly (readonly [DictKey, string])[] = [
  ['nav_helpdesk', '/helpdesk'],
  ['nav_amenities', '/amenities'],
  ['nav_venue_booking', '/venue-booking'],
  ['nav_my_bookings', '/my-bookings'],
  ['nav_documents', '/account/documents'],
  ['nav_residents', '/residents'],
];

function SideLink({ label, path }: { label: string; path: string }) {
  const { colors } = useTheme();
  const pathname = usePathname();
  const active = pathname === path || (path !== '/home' && pathname.startsWith(path));
  return (
    <Pressable
      accessibilityRole="link"
      onPress={() => router.push(path as never)}
      style={({ hovered, pressed }: { hovered?: boolean; pressed: boolean }) => ({
        paddingVertical: 9, paddingHorizontal: space.s3, borderRadius: 8,
        backgroundColor: active ? colors.primary100 : hovered || pressed ? colors.canvas1 : 'transparent',
      })}
    >
      <AppText variant="bodyMd" weight={active ? '600' : '400'}
        style={{ color: active ? colors.primary900 : colors.textSecondary }}>
        {label}
      </AppText>
    </Pressable>
  );
}

function Sidebar() {
  const { colors, scheme, toggle } = useTheme();
  const { t } = useT();
  const { user, signOut } = useSession();
  const isAdmin = user?.role === 'SUB_ADMIN' || user?.role === 'MASTER_ADMIN';
  const logout = async () => {
    await signOut();
    router.replace('/login');
  };
  return (
    <View style={{ width: 248, borderRightWidth: 0.5, borderRightColor: colors.borderSoft, backgroundColor: colors.canvas0 }}>
      <Pressable
        accessibilityRole="link"
        accessibilityLabel="Bero home"
        onPress={() => router.push('/home')}
        style={{ paddingHorizontal: space.s4, height: 56, justifyContent: 'center' }}
      >
        <BrandLockup markHeight={26} />
      </Pressable>
      <Hairline />
      {/* Active-society switcher (multi-society). Renders the current society and,
          for members of 2+ societies, a dropdown to switch the whole app scope. */}
      <View style={{ paddingHorizontal: space.s3, paddingVertical: space.s2 }}>
        <SocietySwitcher />
      </View>
      <Hairline />
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: space.s3, gap: 2 }} showsVerticalScrollIndicator={false}>
        {NAV_MAIN.map(([label, path]) => <SideLink key={path} label={t(label)} path={path} />)}
        {isAdmin || user?.role === 'COMMITTEE' ? (
          <>
            <View style={{ height: space.s4 }} />
            <AppText variant="label" tone="tertiary" style={{ paddingHorizontal: space.s3, marginBottom: 6 }}>{t('nav_grp_management')}</AppText>
            {isAdmin ? <SideLink label={t('nav_society_admin')} path="/admin" /> : null}
            {user?.role === 'COMMITTEE' ? <SideLink label={t('nav_committee_desk')} path="/committee" /> : null}
          </>
        ) : null}
        <View style={{ height: space.s4 }} />
        <AppText variant="label" tone="tertiary" style={{ paddingHorizontal: space.s3, marginBottom: 6 }}>{t('tab_community')}</AppText>
        {NAV_COMMUNITY.map(([label, path]) => <SideLink key={path} label={t(label)} path={path} />)}
        <View style={{ height: space.s4 }} />
        <AppText variant="label" tone="tertiary" style={{ paddingHorizontal: space.s3, marginBottom: 6 }}>{t('tab_gate')}</AppText>
        {NAV_GATE.map(([label, path]) => <SideLink key={path} label={t(label)} path={path} />)}
        <View style={{ height: space.s4 }} />
        <AppText variant="label" tone="tertiary" style={{ paddingHorizontal: space.s3, marginBottom: 6 }}>{t('nav_grp_living')}</AppText>
        {NAV_LIVING.map(([label, path]) => <SideLink key={path} label={t(label)} path={path} />)}
      </ScrollView>
      <Hairline />
      <View style={{ padding: space.s3, flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
        <ArchAvatar name={user?.fullName ?? 'Resident'} size={34} tone="primary" />
        <View style={{ flex: 1, minWidth: 0 }}>
          <AppText variant="bodySm" weight="600" numberOfLines={1}>{user?.fullName ?? t('nav_signed_in')}</AppText>
          {user?.flat?.code ? <AppText variant="bodySm" tone="tertiary">{user.flat.code}</AppText> : null}
        </View>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={scheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          onPress={toggle}
          style={({ hovered, pressed }: { hovered?: boolean; pressed: boolean }) => ({
            width: 34, height: 34, borderRadius: 8, alignItems: 'center', justifyContent: 'center',
            borderWidth: 1, borderColor: colors.borderSoft,
            backgroundColor: hovered || pressed ? colors.canvas1 : 'transparent',
          })}
        >
          <Icon name={scheme === 'dark' ? 'sun' : 'moon'} size={16} color={colors.textSecondary} />
        </Pressable>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('sign_out')}
          onPress={() => void logout()}
          style={({ hovered, pressed }: { hovered?: boolean; pressed: boolean }) => ({
            paddingHorizontal: space.s3, paddingVertical: 8, borderRadius: 8,
            borderWidth: 1, borderColor: colors.borderFirm,
            backgroundColor: hovered || pressed ? colors.danger50 : 'transparent',
          })}
        >
          <AppText variant="bodySm" weight="600" style={{ color: colors.danger500 }}>{t('sign_out')}</AppText>
        </Pressable>
      </View>
    </View>
  );
}

/** Routes that render WITHOUT the resident sidebar: public/auth/onboarding
 * (no session yet), the marketing home, and admin/committee (own shell). */
function noShellPath(p: string): boolean {
  if (p === '/' || p === '/committee') return true;
  return ['/login', '/onboarding', '/p/', '/i/', '/admin', '/magic', '/forgot', '/reset', '/_sitemap', '/+not-found', '/platform', '/claim-owner']
    .some((prefix) => p.startsWith(prefix));
}

/** Wraps the whole app: on desktop web, an authenticated resident on an app
 * route gets the persistent sidebar beside the routed content. Everywhere else
 * (mobile, logged-out, public/admin routes) it renders children untouched. */
export function DesktopFrame({ children }: { children: React.ReactNode }) {
  const { colors } = useTheme();
  const { user } = useSession();
  const { width } = useWindowDimensions();
  const pathname = usePathname();
  const wide = Platform.OS === 'web' && width >= WIDE;
  if (!wide || !user || noShellPath(pathname)) return <>{children}</>;
  return (
    <View style={{ flex: 1, flexDirection: 'row', backgroundColor: colors.canvas0 }}>
      <Sidebar />
      <View style={{ flex: 1 }}>
        <InShellProvider>{children}</InShellProvider>
      </View>
    </View>
  );
}
